import {getTestProps} from '@benchling/js-test-utils';
import {
  ActionIcon,
  Alert,
  CloseButton,
  createStyles,
  Divider,
  Group,
  Loader,
  Stack,
  Tabs,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import React, {useCallback, useEffect, useState} from 'react';

import {useTextContentFromUrl as useFileContent} from 'files/preview/useTextContentFromUrl';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {SkillFileContentRenderer} from 'llm/chat-library/shared/SkillFileContentBlocks';
import {isTextPreviewablePath} from 'llm/chat-library/shared/SkillFileUtils';
import SkillAccessTab from 'llm/chat-library/skills/SkillAccessTab';
import SkillOverflowMenu from 'llm/chat-library/skills/SkillOverflowMenu';
import SkillVersionHistoryPanel from 'llm/chat-library/skills/SkillVersionHistoryPanel';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  AGENT_SKILL_VERSION_FILE_PRESIGNED_URL_QUERY,
  AgentSkillVersionFilePresignedUrlQueryData,
  AgentSkillVersionFilePresignedUrlQueryVariables,
  canManageSkillAccess,
  downloadSkillVersionFile,
  SkillSummary,
  SkillVersion,
  SkillVersionFile,
} from './SkillsDAO';

export enum SkillDetailTab {
  DETAIL = 'DETAIL',
  ACCESS = 'ACCESS',
}

interface SkillDetailPanelProps {
  skill: SkillSummary;
  onClose: () => void;
  onUpdate?: () => void;
  onArchive: (skillId: string, reason: string) => void;
  /**
   * Tab to activate when this skill's detail view opens. Used so that opening the view
   * via the "Manage access" menu option lands directly on the Manage access tab. Falls
   * back to the Skill detail tab when omitted or when the requested tab isn't available.
   */
  initialTab?: SkillDetailTab;
}

const TOOLBAR_WIDTH = 45;
const HISTORY_PANEL_WIDTH = 300;

const useStyles = createStyles((theme) => ({
  outerContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    overflow: 'hidden',
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: TOOLBAR_WIDTH,
    flexShrink: 0,
    paddingTop: theme.spacing.sm,
    gap: theme.spacing.xs,
    borderLeft: `1px solid ${theme.colors.gray[3]}`,
  },
  toolbarIcon: {
    backgroundColor: 'transparent',
    color: theme.colors.gray[6],
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.colors.gray[9],
    },
  },
  toolbarIconActive: {
    color: theme.colors.blue[6],
    '&:hover': {
      color: theme.colors.blue[7],
    },
  },
  historyPanel: {
    position: 'absolute',
    top: 0,
    right: TOOLBAR_WIDTH,
    bottom: 0,
    width: HISTORY_PANEL_WIDTH,
    backgroundColor: theme.white,
    borderLeft: `1px solid ${theme.colors.gray[3]}`,
    boxShadow: '-2px 0 6px 0 rgba(162, 185, 190, 0.3)',
    zIndex: 10,
    overflow: 'hidden',
  },
  tabsRoot: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  tab: {
    padding: theme.spacing.md,
    '&[data-active]': {
      borderBottomWidth: 3,
    },
  },
  tabsList: {
    padding: `${theme.spacing.none} ${theme.spacing.md}`,
  },
  tabsPanel: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing.md,
  },
  contentArea: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  containerFileView: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing.md,
    overflow: 'hidden',
  },
  fileViewPathGroup: {
    minWidth: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
  },
  description: {
    whiteSpace: 'pre-wrap',
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  fileItem: {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.sm,
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  fileViewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  fileViewContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflowY: 'auto',
  },
  skillFilePreviewFill: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  versionBannerLink: {
    display: 'inline',
    fontSize: 'inherit',
    cursor: 'pointer',
    color: theme.colors.blue[6],
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

interface ViewingFile {
  file: SkillVersionFile;
}

interface SkillFileListItem {
  id: string;
  label: string;
  onClick: () => void;
}

interface SkillFilesSectionProps {
  fileItems: Array<SkillFileListItem>;
}

function SkillFilesSection({fileItems}: SkillFilesSectionProps): JSX.Element | null {
  const {classes} = useStyles();
  if (fileItems.length === 0) {
    return null;
  }

  return (
    <>
      <Divider my="sm" />
      <Stack spacing="xs">
        <Text size="sm" fw={600}>
          Files ({fileItems.length})
        </Text>
        <div className={classes.fileList}>
          {fileItems.map((file) => (
            <UnstyledButton
              key={file.id}
              className={classes.fileItem}
              onClick={file.onClick}
              {...getTestProps({component: 'SkillDetailPanel', element: 'fileItem'})}
            >
              <Text size="xs" ff="monospace">
                {file.label}
              </Text>
            </UnstyledButton>
          ))}
        </div>
      </Stack>
    </>
  );
}

function SkillVersionFileView({
  file,
  skillId,
  versionId,
  onBack,
  onClose,
}: {
  file: SkillVersionFile;
  skillId: string;
  // Set only while viewing a prior version; null means the skill's current version.
  versionId: string | null;
  onBack: () => void;
  onClose: () => void;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const isTextPreviewable = isTextPreviewablePath(file.name);
  const presignedUrlResult = useSkippableBenchlingQuery<
    AgentSkillVersionFilePresignedUrlQueryData,
    AgentSkillVersionFilePresignedUrlQueryVariables
  >(AGENT_SKILL_VERSION_FILE_PRESIGNED_URL_QUERY, {
    variables: {s3ExternalFileId: file.s3ExternalFileId},
    fetchPolicy: 'network-only',
    skip: !isTextPreviewable,
  });

  const presignedUrl =
    presignedUrlResult.type === QueryResultType.SUCCESS
      ? (presignedUrlResult.data.agentSkillVersionFilePresignedUrl ?? null)
      : null;

  const isUrlLoading = presignedUrlResult.type === QueryResultType.PENDING;
  const urlError =
    presignedUrlResult.type === QueryResultType.ERROR ||
    (presignedUrlResult.type === QueryResultType.SUCCESS && presignedUrl == null);

  const {
    content,
    isLoading: isContentLoading,
    error: contentError,
  } = useFileContent(isTextPreviewable ? presignedUrl : null);
  const isLoading = isUrlLoading || (presignedUrl != null && isContentLoading);
  const error = urlError ? 'Failed to load file URL.' : contentError;

  return (
    <div
      className={classes.containerFileView}
      {...getTestProps({component: 'SkillDetailPanel', element: 'versionFileView'})}
    >
      <div className={classes.fileViewHeader}>
        <Group spacing="xs" noWrap={true} className={classes.fileViewPathGroup}>
          <ActionIcon aria-label="Back to file list" onClick={onBack} size="sm" variant="subtle">
            <BenchlingIcon icon="chevron-left" />
          </ActionIcon>
          <Text size="xs" ff="monospace" truncate={true}>
            {isLoading ? 'Loading…' : file.name}
          </Text>
        </Group>
        <Group spacing="xs" noWrap={true}>
          <Tooltip label="Download">
            <ActionIcon
              aria-label="Download file"
              onClick={() => {
                downloadSkillVersionFile(skillId, file.s3ExternalFileId, versionId ?? undefined);
              }}
              size="sm"
              variant="outline"
              color="gray"
              {...getTestProps({component: 'SkillDetailPanel', element: 'downloadFileButton'})}
            >
              <BenchlingIcon icon="download" />
            </ActionIcon>
          </Tooltip>
          <CloseButton
            onClick={onClose}
            size="sm"
            {...getTestProps({component: 'SkillDetailPanel', element: 'closeButton'})}
          />
        </Group>
      </div>

      <Divider my="sm" />

      {isLoading && <Loader size="xs" />}
      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
      {!isLoading && !error && (
        <div className={classes.fileViewContent}>
          {!isTextPreviewable ? (
            <SkillFileContentRenderer
              virtualPath={file.name}
              content=""
              className={classes.skillFilePreviewFill}
            />
          ) : content != null ? (
            <SkillFileContentRenderer
              virtualPath={file.name}
              content={content}
              className={classes.skillFilePreviewFill}
            />
          ) : (
            <Text size="xs" c="dimmed">
              File content could not be loaded.
            </Text>
          )}
        </div>
      )}
    </div>
  );
}

export default function SkillDetailPanel({
  skill,
  onClose,
  onUpdate,
  onArchive,
  initialTab,
}: SkillDetailPanelProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useStyles();

  const canManageSharing = canManageSkillAccess(skill);

  // Only honor a requested Manage access tab when that tab is actually rendered.
  const resolvedInitialTab =
    initialTab === SkillDetailTab.ACCESS && canManageSharing ? SkillDetailTab.ACCESS : SkillDetailTab.DETAIL;

  const [viewingFile, setViewingFile] = useState<ViewingFile | null>(null);
  const [activeTab, setActiveTab] = useState<SkillDetailTab>(resolvedInitialTab);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  const [viewingVersion, setViewingVersion] = useState<SkillVersion | null>(null);

  useEffect(() => {
    setViewingFile(null);
    setViewingVersion(null);
    setShowVersionHistory(false);
    setActiveTab(resolvedInitialTab);
  }, [skill.id, resolvedInitialTab]);

  const viewingVersionId = viewingVersion?.id;
  useEffect(() => {
    setViewingFile(null);
  }, [viewingVersionId]);

  const handleTabChange = useCallback((tab: string | null) => {
    if (tab == null || !Object.values(SkillDetailTab).includes(tab as SkillDetailTab)) {
      return;
    }
    setActiveTab(tab as SkillDetailTab);
    setViewingFile(null);
  }, []);

  const handleVersionSelected = useCallback((version: SkillVersion | null) => {
    setViewingVersion(version);
    if (version != null) {
      setActiveTab(SkillDetailTab.DETAIL);
    }
  }, []);

  const handleViewLatest = useCallback(() => {
    setViewingVersion(null);
  }, []);

  const displayName = viewingVersion?.name ?? skill.name;
  const displayDescription = viewingVersion?.description ?? skill.description;
  const displayCreatorName = viewingVersion ? viewingVersion.creatorName : skill.creatorName;
  const displayDate = viewingVersion
    ? new Date(viewingVersion.createdAt).toLocaleDateString()
    : skill.createdAt
      ? new Date(skill.createdAt).toLocaleDateString()
      : null;

  const displayFiles = viewingVersion ? viewingVersion.files : skill.skillFiles;
  const fileItems: Array<SkillFileListItem> = displayFiles.map((file) => ({
    id: file.s3ExternalFileId,
    label: file.name,
    onClick: () => {
      setViewingFile({file});
    },
  }));

  const onBackFromFile = useCallback(() => {
    setViewingFile(null);
  }, []);

  const detailTabContent =
    viewingFile != null ? (
      <SkillVersionFileView
        file={viewingFile.file}
        skillId={skill.id}
        versionId={viewingVersion?.id ?? null}
        onBack={onBackFromFile}
        onClose={onClose}
      />
    ) : (
      <div
        className={classes.contentArea}
        {...getTestProps({component: 'SkillDetailPanel', element: 'detailContent'})}
      >
        {viewingVersion && (
          <Alert
            color="yellow"
            mb="sm"
            {...getTestProps({component: 'SkillDetailPanel', element: 'versionBanner'})}
          >
            <Text size="sm">
              You are viewing a previous version of this skill.{' '}
              <UnstyledButton type="button" className={classes.versionBannerLink} onClick={handleViewLatest}>
                View the latest version.
              </UnstyledButton>
            </Text>
          </Alert>
        )}

        {displayDescription ? (
          <Text size="sm" className={classes.description}>
            {displayDescription}
          </Text>
        ) : (
          <Text size="sm" c="dimmed">
            No description provided.
          </Text>
        )}

        <SkillFilesSection fileItems={fileItems} />
      </div>
    );

  return (
    <div
      className={classes.outerContainer}
      {...getTestProps({component: 'SkillDetailPanel', element: 'root'})}
    >
      <div className={classes.mainColumn}>
        <div className={classes.header}>
          <div className={classes.titleBlock}>
            <Text fw={700} size="xl">
              {displayName}
            </Text>
            {displayCreatorName && (
              <Text size="sm" c="dimmed">
                by {displayCreatorName}
              </Text>
            )}
            {displayDate && (
              <Text size="xs" c="dimmed">
                Created {displayDate}
              </Text>
            )}
          </div>
          <Group spacing={4} noWrap={true}>
            <SkillOverflowMenu
              skill={skill}
              testComponentName="SkillDetailPanel"
              onArchive={onArchive}
              onManageAccess={() => {
                setActiveTab(SkillDetailTab.ACCESS);
              }}
              viewingVersionId={viewingVersionId}
            />
            <CloseButton
              onClick={onClose}
              size="sm"
              {...getTestProps({component: 'SkillDetailPanel', element: 'closeButton'})}
            />
          </Group>
        </div>

        <Tabs value={activeTab} onTabChange={handleTabChange} className={classes.tabsRoot}>
          <Tabs.List className={classes.tabsList}>
            <Tabs.Tab
              value={SkillDetailTab.DETAIL}
              className={classes.tab}
              {...getTestProps({component: 'SkillDetailPanel', element: 'tab', key: 'Detail'})}
              data-amp-safe={false}
            >
              Skill detail
            </Tabs.Tab>
            {canManageSharing && (
              <Tabs.Tab
                value={SkillDetailTab.ACCESS}
                className={classes.tab}
                {...getTestProps({component: 'SkillDetailPanel', element: 'tab', key: 'Access'})}
                data-amp-safe={false}
              >
                Manage access
              </Tabs.Tab>
            )}
          </Tabs.List>

          <Tabs.Panel value={SkillDetailTab.DETAIL} className={classes.tabsPanel}>
            {detailTabContent}
          </Tabs.Panel>

          {canManageSharing && (
            <Tabs.Panel value={SkillDetailTab.ACCESS} className={classes.tabsPanel}>
              <SkillAccessTab skill={skill} onUpdate={onUpdate} />
            </Tabs.Panel>
          )}
        </Tabs>
      </div>

      <div className={classes.toolbar}>
        <Tooltip label="History" position="left" withArrow={true}>
          <ActionIcon
            size="lg"
            variant="subtle"
            className={cx(classes.toolbarIcon, {
              [classes.toolbarIconActive]: showVersionHistory,
            })}
            onClick={() => {
              setShowVersionHistory((prev) => !prev);
            }}
            aria-label="Version history"
            {...getTestProps({component: 'SkillDetailPanel', element: 'versionHistoryIcon'})}
          >
            <BenchlingIcon icon="history" />
          </ActionIcon>
        </Tooltip>
      </div>

      {showVersionHistory && viewingFile == null && (
        <div className={classes.historyPanel}>
          <SkillVersionHistoryPanel
            skill={skill}
            onClose={() => {
              setShowVersionHistory(false);
            }}
            onReverted={() => {
              setShowVersionHistory(false);
              setViewingVersion(null);
              setActiveTab(SkillDetailTab.DETAIL);
              onUpdate?.();
            }}
            onVersionSelected={handleVersionSelected}
            selectedVersionId={viewingVersion?.id ?? null}
          />
        </div>
      )}
    </div>
  );
}
